/**
 * Autonomous Governance Response Page (F039)
 * Route: /autonomous-governance | Nav: Model > Governance Approvals
 *
 * The #1-priority control-plane page: a human-in-the-loop approval queue for automated policy
 * responses to governance drift, severe REGE verdicts, and risk paths. Three tabs sharing one
 * page shell: Proposals (the Rule-4 approval queue — the star), Action Log (response history),
 * and Policies (the rules that generate proposals).
 *
 * Binds to `AutonomousGovernanceController` (`api/portal/autonomous-governance`). Mirrors
 * `McpGovernancePage.tsx` (F032) for list+detail+action-dialog structure, badge semantic-color-map
 * functions, `useAdaptiveGridData` wiring, `useToastController`/`Toaster`, and the
 * mutation+`invalidateQueries` pattern. F039 does NOT expose a public "enforce" endpoint —
 * enforcement happens only through F034 REGE, so there is no create/enforce action here beyond
 * policy authoring and the approve/reject/revert triad. Create Policy form lives in
 * `src/features/autonomous-governance/forms/`.
 */
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Body2,
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Switch,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  Tooltip,
  useId,
  useToastController,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { BadgeProps, SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import {
  AddRegular,
  ArrowUndoRegular,
  CheckmarkCircle24Regular,
  ChevronRightRegular,
  DismissRegular,
  GavelRegular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { autonomousGovernanceApi } from '../services/api';
import type {
  ResponseAction,
  ResponseActionStatus,
  ResponseActionType,
  ResponseImpactClass,
  ResponsePolicy,
  ResponseProposal,
  ResponseReversibility,
  ResponseSeverity,
  ResponseTriggerKind,
} from '../types';
import { ResponsePolicyForm } from '../features/autonomous-governance/forms';
import {
  ListFrame,
  Pager,
  actionsColumnWidth,
  useAdaptiveGridData,
  useDebouncedValue,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult } from '../components/list';
import {
  DetailDrawer,
  DetailSection,
  DetailField,
  DetailMetadataGrid,
  DetailBadges,
} from '../components/common/DetailDrawer';
import { MetricCard } from '../components/design-system';
import { PageHeader } from '../components/common/PageHeader';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

const RESPONSE_ACTION_STATUS_OPTIONS: ResponseActionStatus[] = [
  'Proposed',
  'AwaitingApproval',
  'Applied',
  'Suppressed',
  'Reverted',
  'Expired',
];

const RESPONSE_ACTION_TYPE_FILTER_OPTIONS: ResponseActionType[] = [
  'RevokePermissions',
  'ReduceExecutionScope',
  'DowngradeToReadOnly',
  'DisableAutomation',
  'IsolateConnector',
  'InsertApprovalGate',
  'TerminateExecutionChain',
  'ForceSandboxMode',
];

// ---------------------------------------------------------------------------
// Badge color mapping — semantic map per enum, not raw hex (per List & Grid Standard §15).
// ---------------------------------------------------------------------------

/** State column — verbatim from the spec: Applied=success, AwaitingApproval=warning,
 *  Proposed=informative, Reverted=severe, Suppressed=subtle, Expired=danger. */
const actionStateColor = (state?: ResponseActionStatus): BadgeProps['color'] => {
  switch (state) {
    case 'Applied': return 'success';
    case 'AwaitingApproval': return 'warning';
    case 'Proposed': return 'informative';
    case 'Reverted': return 'severe';
    case 'Suppressed': return 'subtle';
    case 'Expired': return 'danger';
    default: return 'informative';
  }
};

const impactClassColor = (impact?: ResponseImpactClass): BadgeProps['color'] => {
  switch (impact) {
    case 'Low': return 'success';
    case 'Medium': return 'warning';
    case 'High': return 'danger';
    default: return 'informative';
  }
};

const reversibilityColor = (r?: ResponseReversibility): BadgeProps['color'] => {
  switch (r) {
    case 'Reversible': return 'success';
    case 'PartiallyReversible': return 'warning';
    case 'Irreversible': return 'danger';
    default: return 'informative';
  }
};

const severityColor = (s?: ResponseSeverity): BadgeProps['color'] => {
  switch (s) {
    case 'Critical': return 'danger';
    case 'High': return 'severe';
    case 'Medium': return 'warning';
    case 'Low': return 'success';
    default: return 'informative';
  }
};

const triggerKindColor = (t?: ResponseTriggerKind): BadgeProps['color'] => {
  switch (t) {
    case 'SevereRegeVerdict': return 'danger';
    case 'RiskPath': return 'severe';
    case 'GovernanceDrift': return 'warning';
    default: return 'informative';
  }
};

/** Response action type — graded roughly by blast radius: full lockdown actions are `danger`,
 *  strong containment is `severe`, scope-reduction is `warning`, and the softest gate
 *  (approval-insertion, no capability removed) is `informative`. */
const actionTypeColor = (a?: ResponseActionType): BadgeProps['color'] => {
  switch (a) {
    case 'RevokePermissions':
    case 'TerminateExecutionChain':
      return 'danger';
    case 'DisableAutomation':
    case 'IsolateConnector':
    case 'ForceSandboxMode':
      return 'severe';
    case 'ReduceExecutionScope':
    case 'DowngradeToReadOnly':
      return 'warning';
    case 'InsertApprovalGate':
      return 'informative';
    default:
      return 'informative';
  }
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  summarySection: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  statCardRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  cappedNote: {
    color: tokens.colorNeutralForeground3,
  },
  tabsSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  tabHeader: {
    flexShrink: 0,
  },
  tabPanel: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  toolbarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  filterGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  filterDropdown: {
    minWidth: '10rem',
  },
  textFilterInput: {
    minWidth: '10rem',
  },
  formDialogSurface: {
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
  justificationCell: {
    maxWidth: '20rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const crumbTitle = 'Governance Approvals';

// ---------------------------------------------------------------------------
// AutonomousGovernanceSummaryPanel — Pending Proposals / Applied / Enabled Policies stat cards.
// ---------------------------------------------------------------------------

/** Bounded sample for the Enabled Policies count — `getPolicies` has no `enabled`-only filter, so
 *  the count is computed client-side over a capped sample, mirroring `McpSummaryPanel`'s pattern. */
const POLICY_SUMMARY_SAMPLE = 200;

const AutonomousGovernanceSummaryPanel: FC = () => {
  const styles = useStyles();

  const pendingQuery = useQuery({
    queryKey: ['response-governance-summary', 'proposals'],
    // `getProposals` returns only the pending Rule-4 queue, so `totalCount` IS the pending count.
    queryFn: () => autonomousGovernanceApi.getProposals(0, 1),
  });
  const appliedQuery = useQuery({
    queryKey: ['response-governance-summary', 'applied'],
    queryFn: () => autonomousGovernanceApi.getActions({ state: 'Applied' }, 0, 1),
  });
  const policiesQuery = useQuery({
    queryKey: ['response-governance-summary', 'policies'],
    queryFn: () => autonomousGovernanceApi.getPolicies(0, POLICY_SUMMARY_SAMPLE),
  });

  const isLoading = pendingQuery.isLoading || appliedQuery.isLoading || policiesQuery.isLoading;
  const isError = pendingQuery.isError || appliedQuery.isError || policiesQuery.isError;

  if (isLoading) {
    return (
      <div className={styles.summarySection}>
        <LoadingSkeleton rows={1} columns={3} />
      </div>
    );
  }
  if (isError) {
    return (
      <div className={styles.summarySection}>
        <ErrorState
          message="Unable to load the governance approvals summary."
          onRetry={() => {
            void pendingQuery.refetch();
            void appliedQuery.refetch();
            void policiesQuery.refetch();
          }}
        />
      </div>
    );
  }

  const pendingCount = pendingQuery.data?.totalCount ?? 0;
  const appliedCount = appliedQuery.data?.totalCount ?? 0;
  const policiesData = policiesQuery.data;
  const enabledCount = policiesData ? policiesData.items.filter((p) => p.enabled).length : 0;
  const cappedPolicies = policiesData ? policiesData.totalCount > policiesData.items.length : false;

  return (
    <div className={styles.summarySection}>
      <div className={styles.statCardRow}>
        <MetricCard
          title="Pending Proposals"
          value={pendingCount}
          icon={<GavelRegular />}
          accentColor={tokens.colorPaletteMarigoldForeground1}
        />
        <MetricCard
          title="Applied"
          value={appliedCount}
          icon={<CheckmarkCircle24Regular />}
          accentColor={tokens.colorPaletteGreenForeground1}
        />
        <MetricCard
          title="Enabled Policies"
          value={cappedPolicies ? `${enabledCount}+` : enabledCount}
          icon={<GavelRegular />}
          accentColor={tokens.colorBrandForeground1}
        />
      </div>
      {cappedPolicies && policiesData && (
        <Caption1 className={styles.cappedNote}>
          Enabled Policies reflects the first {policiesData.items.length} of {policiesData.totalCount} policies.
        </Caption1>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 1 — Proposals (the Rule-4 approval queue)
// ---------------------------------------------------------------------------

const PROPOSALS_PAGE_SIZE = 25;

const ProposalsTab: FC<{ toasterId: string }> = ({ toasterId }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  const [page, setPage] = useState(1);
  const skip = (page - 1) * PROPOSALS_PAGE_SIZE;

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['response-proposals', skip, PROPOSALS_PAGE_SIZE],
    queryFn: () => autonomousGovernanceApi.getProposals(skip, PROPOSALS_PAGE_SIZE),
  });

  // A SINGLE decision dialog shared by Approve/Reject (mode-switched), rather than two always-mounted
  // `Dialog` instances — Fluent's Dialog registers a tabster modal focus-trap on mount, and two
  // co-existing modal Dialogs (one open, one closed-but-mounted) can leave the open one's surface
  // stuck `aria-hidden="true"` when its sibling closed Dialog's tabster dummy nodes are also present.
  const [decision, setDecision] = useState<{ proposal: ResponseProposal; mode: 'approve' | 'reject' } | null>(
    null
  );
  const [decisionNote, setDecisionNote] = useState('');
  const closeDecisionDialog = () => {
    setDecision(null);
    setDecisionNote('');
  };

  const invalidateAfterDecision = () => {
    queryClient.invalidateQueries({ queryKey: ['response-proposals'] });
    queryClient.invalidateQueries({ queryKey: ['response-actions'] });
    queryClient.invalidateQueries({ queryKey: ['response-governance-summary'] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ approvalRequestId, note }: { approvalRequestId: string; note?: string }) =>
      autonomousGovernanceApi.approveProposal(approvalRequestId, note),
    onSuccess: (action) => {
      invalidateAfterDecision();
      dispatchToast(
        <Toast>
          <ToastTitle>Proposal Approved</ToastTitle>
          <ToastBody>{action.subjectExternalId} — response applied.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
      closeDecisionDialog();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ approvalRequestId, note }: { approvalRequestId: string; note?: string }) =>
      autonomousGovernanceApi.rejectProposal(approvalRequestId, note),
    onSuccess: (action) => {
      invalidateAfterDecision();
      dispatchToast(
        <Toast>
          <ToastTitle>Proposal Rejected</ToastTitle>
          <ToastBody>{action.subjectExternalId} — response suppressed.</ToastBody>
        </Toast>,
        { intent: 'warning', timeout: 5000 }
      );
      closeDecisionDialog();
    },
  });

  const isDecisionPending = approveMutation.isPending || rejectMutation.isPending;

  const handleDecisionConfirm = () => {
    if (!decision) return;
    const note = decisionNote.trim() || undefined;
    if (decision.mode === 'approve') {
      approveMutation.mutate({ approvalRequestId: decision.proposal.approvalRequestId, note });
    } else {
      rejectMutation.mutate({ approvalRequestId: decision.proposal.approvalRequestId, note });
    }
  };

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(Math.ceil(totalCount / PROPOSALS_PAGE_SIZE), 1);
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;
  const proposals = data?.items ?? [];

  const columnCount = 6; // subject, action, impact, justification, expires, actions
  const actionButtonCount = 2; // Approve + Reject

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the approval queue." onRetry={refetch} />;
  } else if (proposals.length === 0) {
    listBody = (
      <EmptyState
        icon={<GavelRegular />}
        title="No Pending Proposals"
        description="No responses are currently awaiting human approval."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Response proposals">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '100%' }}>Subject</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Impact</TableHeaderCell>
              <TableHeaderCell>Justification</TableHeaderCell>
              <TableHeaderCell>Expires</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {proposals.map((item) => (
              <TableRow key={item.approvalRequestId}>
                <TableCell>
                  <Text weight="semibold">{item.subjectExternalId}</Text>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={actionTypeColor(item.action)}>{item.action}</Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={impactClassColor(item.impactClass)}>{item.impactClass}</Badge>
                </TableCell>
                <TableCell>
                  <Tooltip content={item.justification} relationship="description">
                    <Body2 className={styles.justificationCell}>{item.justification}</Body2>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Caption1>{new Date(item.expiresAt).toLocaleString()}</Caption1>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                    <Tooltip content="Approve" relationship="label">
                      <Button
                        appearance="subtle"
                        icon={<CheckmarkCircle24Regular />}
                        size="small"
                        aria-label="Approve proposal"
                        onClick={() => setDecision({ proposal: item, mode: 'approve' })}
                      />
                    </Tooltip>
                    <Tooltip content="Reject" relationship="label">
                      <Button
                        appearance="subtle"
                        icon={<DismissRegular />}
                        size="small"
                        aria-label="Reject proposal"
                        onClick={() => setDecision({ proposal: item, mode: 'reject' })}
                      />
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.tabPanel}>
      <ListFrame>{listBody}</ListFrame>
      <Pager
        page={page}
        totalPages={totalPages}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        totalCount={totalCount}
        onPageChange={setPage}
        testId="response-proposals-pager"
      />

      {/* Approve / Reject Proposal — a single dialog, mode-switched (see the tabster comment above). */}
      <Dialog
        open={decision !== null}
        onOpenChange={(_, data2) => {
          if (!data2.open) closeDecisionDialog();
        }}
      >
        <DialogSurface>
          <DialogTitle>{decision?.mode === 'reject' ? 'Reject Response' : 'Approve Response'}</DialogTitle>
          <DialogBody>
            <DialogContent>
              {decision && decision.mode === 'approve' && (
                <Text>
                  Approve <strong>{decision.proposal.action}</strong> for{' '}
                  <strong>{decision.proposal.subjectExternalId}</strong>? The response will be applied via REGE
                  with your approval authority.
                </Text>
              )}
              {decision && decision.mode === 'reject' && (
                <Text>
                  Reject <strong>{decision.proposal.action}</strong> for{' '}
                  <strong>{decision.proposal.subjectExternalId}</strong>? The response moves to Suppressed with
                  zero REGE calls.
                </Text>
              )}
              <Field label="Note" hint="Optional context recorded against this decision">
                <Textarea
                  value={decisionNote}
                  onChange={(_, data2) => setDecisionNote(data2.value)}
                  placeholder="Optional note"
                  resize="vertical"
                  disabled={isDecisionPending}
                />
              </Field>
              <div className={styles.dialogActions}>
                <Button appearance="secondary" onClick={closeDecisionDialog} disabled={isDecisionPending}>
                  Cancel
                </Button>
                {decision?.mode === 'reject' ? (
                  <Button
                    appearance="primary"
                    disabled={isDecisionPending}
                    icon={rejectMutation.isPending ? <Spinner size="tiny" /> : <DismissRegular />}
                    onClick={handleDecisionConfirm}
                  >
                    {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
                  </Button>
                ) : (
                  <Button
                    appearance="primary"
                    disabled={isDecisionPending}
                    icon={approveMutation.isPending ? <Spinner size="tiny" /> : <CheckmarkCircle24Regular />}
                    onClick={handleDecisionConfirm}
                  >
                    {approveMutation.isPending ? 'Approving…' : 'Approve'}
                  </Button>
                )}
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2 — Action Log
// ---------------------------------------------------------------------------

const ACTIONS_GRID_THRESHOLD = 200;
const ACTIONS_PAGE_SIZE = 25;

async function fetchActionsPage(
  filter: { state?: ResponseActionStatus; action?: ResponseActionType; subject?: string },
  params: ListQueryParams
): Promise<PagedResult<ResponseAction>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result = await autonomousGovernanceApi.getActions(filter, skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

const ActionDetailDrawer: FC<{ responseId: string | null; onClose: () => void; toasterId: string }> = ({
  responseId,
  onClose,
  toasterId,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['response-action-detail', responseId],
    queryFn: () => autonomousGovernanceApi.getAction(responseId!),
    enabled: responseId !== null,
  });

  const [isRevertDialogOpen, setIsRevertDialogOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [revertReasonError, setRevertReasonError] = useState<string | undefined>();

  const closeRevertDialog = () => {
    setIsRevertDialogOpen(false);
    setRevertReason('');
    setRevertReasonError(undefined);
  };

  const revertMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => autonomousGovernanceApi.revertAction(id, reason),
    onSuccess: (action) => {
      queryClient.invalidateQueries({ queryKey: ['response-actions'] });
      queryClient.invalidateQueries({ queryKey: ['response-action-detail', responseId] });
      queryClient.invalidateQueries({ queryKey: ['response-governance-summary'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Response Reverted</ToastTitle>
          <ToastBody>{action.subjectExternalId} — compensating directive issued.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
      closeRevertDialog();
    },
  });

  const handleRevertSubmit = () => {
    if (!revertReason.trim()) {
      setRevertReasonError('Reason is required');
      return;
    }
    if (!responseId) return;
    revertMutation.mutate({ id: responseId, reason: revertReason.trim() });
  };

  const canRevert = data && data.state === 'Applied' && data.reversibility !== 'Irreversible';

  return (
    <DetailDrawer open={responseId !== null} onClose={onClose} title="Response Detail" size="medium">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading response…" />
        </div>
      )}
      {isError && <ErrorState message="Unable to load this response's detail." />}
      {data && (
        <>
          <DetailSection title={data.subjectExternalId}>
            <DetailBadges>
              <Badge appearance="tint" color={actionStateColor(data.state)}>{data.state ?? 'No Match'}</Badge>
              {data.action && (
                <Badge appearance="tint" color={actionTypeColor(data.action)}>{data.action}</Badge>
              )}
              {data.impactClass && (
                <Badge appearance="tint" color={impactClassColor(data.impactClass)}>{data.impactClass}</Badge>
              )}
              {data.reversibility && (
                <Badge appearance="tint" color={reversibilityColor(data.reversibility)}>{data.reversibility}</Badge>
              )}
            </DetailBadges>
            <DetailMetadataGrid>
              <DetailField label="Trigger Kind">
                <Badge appearance="outline" color={triggerKindColor(data.triggerKind)}>{data.triggerKind}</Badge>
              </DetailField>
              <DetailField label="Trigger Severity">
                <Badge appearance="tint" color={severityColor(data.triggerSeverity)}>{data.triggerSeverity}</Badge>
              </DetailField>
              <DetailField label="Matched Policy">
                <Text style={{ fontFamily: 'monospace' }}>{data.matchedPolicyExternalId || '—'}</Text>
              </DetailField>
              <DetailField label="Applied By">
                <Text>{data.appliedBy ?? '—'}</Text>
              </DetailField>
              <DetailField label="REGE Decision Id">
                <Text style={{ fontFamily: 'monospace' }}>{data.regeDecisionId ?? '—'}</Text>
              </DetailField>
              <DetailField label="Lineage Entry Id">
                <Text style={{ fontFamily: 'monospace' }}>{data.lineageEntryId ?? '—'}</Text>
              </DetailField>
              <DetailField label="Approval Ref">
                <Text style={{ fontFamily: 'monospace' }}>{data.approvalRef ?? '—'}</Text>
              </DetailField>
              <DetailField label="Created">
                <Text>{data.createdAt ? new Date(data.createdAt).toLocaleString() : '—'}</Text>
              </DetailField>
              <DetailField label="Applied">
                <Text>{data.appliedAt ? new Date(data.appliedAt).toLocaleString() : '—'}</Text>
              </DetailField>
              <DetailField label="Reverted">
                <Text>{data.revertedAt ? new Date(data.revertedAt).toLocaleString() : '—'}</Text>
              </DetailField>
            </DetailMetadataGrid>
          </DetailSection>

          <DetailSection title="Reason" divider>
            <Body2>{data.reason || '—'}</Body2>
          </DetailSection>

          {canRevert && (
            <DetailSection divider>
              <Button
                appearance="secondary"
                icon={<ArrowUndoRegular />}
                onClick={() => setIsRevertDialogOpen(true)}
              >
                Revert
              </Button>
            </DetailSection>
          )}
        </>
      )}

      {/* Revert Response */}
      <Dialog
        open={isRevertDialogOpen}
        onOpenChange={(_, data2) => {
          if (!data2.open) closeRevertDialog();
        }}
      >
        <DialogSurface>
          <DialogTitle>Revert Response</DialogTitle>
          <DialogBody>
            <DialogContent>
              <Field
                label="Reason"
                required
                validationState={revertReasonError ? 'error' : undefined}
                validationMessage={revertReasonError}
              >
                <Textarea
                  value={revertReason}
                  onChange={(_, data2) => {
                    setRevertReason(data2.value);
                    if (revertReasonError) setRevertReasonError(undefined);
                  }}
                  placeholder="Why is this response being reverted?"
                  resize="vertical"
                  disabled={revertMutation.isPending}
                />
              </Field>
              <div className={styles.dialogActions}>
                <Button appearance="secondary" onClick={closeRevertDialog} disabled={revertMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleRevertSubmit}
                  disabled={revertMutation.isPending}
                  icon={revertMutation.isPending ? <Spinner size="tiny" /> : <ArrowUndoRegular />}
                >
                  {revertMutation.isPending ? 'Reverting…' : 'Revert Response'}
                </Button>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </DetailDrawer>
  );
};

const ActionLogTab: FC<{ toasterId: string }> = ({ toasterId }) => {
  const styles = useStyles();

  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState<ResponseActionStatus | ''>('');
  const [actionFilter, setActionFilter] = useState<ResponseActionType | ''>('');
  const [subjectInput, setSubjectInput] = useState('');
  const debouncedSubject = useDebouncedValue(subjectInput, 300);

  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);

  const filter = {
    state: stateFilter || undefined,
    action: actionFilter || undefined,
    subject: debouncedSubject || undefined,
  };
  const filtersKey = `${stateFilter}|${actionFilter}|${debouncedSubject}`;

  const grid = useAdaptiveGridData<ResponseAction>({
    queryKeyBase: ['response-actions', filtersKey],
    fetchPage: (params) => fetchActionsPage(filter, params),
    page,
    pageSize: ACTIONS_PAGE_SIZE,
    threshold: ACTIONS_GRID_THRESHOLD,
  });

  const {
    items: actions,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  const columnCount = 7; // createdAt, action, state, subject, trigger, impact, actions
  const actionButtonCount = 1; // chevron detail

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the action log." onRetry={refetch} />;
  } else if (actions.length === 0) {
    listBody = (
      <EmptyState
        icon={<GavelRegular />}
        title="No Responses Recorded"
        description="No matches for the current filters, or no autonomous responses have run yet for this tenant."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Response action log">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>State</TableHeaderCell>
              <TableHeaderCell style={{ width: '100%' }}>Subject</TableHeaderCell>
              <TableHeaderCell>Trigger</TableHeaderCell>
              <TableHeaderCell>Impact</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {actions.map((item) => (
              <TableRow key={item.responseId}>
                <TableCell>
                  <Caption1>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</Caption1>
                </TableCell>
                <TableCell>
                  {item.action ? (
                    <Badge appearance="tint" color={actionTypeColor(item.action)}>{item.action}</Badge>
                  ) : (
                    <Caption1>—</Caption1>
                  )}
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={actionStateColor(item.state)}>{item.state ?? 'No Match'}</Badge>
                </TableCell>
                <TableCell>
                  <Text>{item.subjectExternalId}</Text>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" color={triggerKindColor(item.triggerKind)}>{item.triggerKind}</Badge>
                </TableCell>
                <TableCell>
                  {item.impactClass ? (
                    <Badge appearance="tint" color={impactClassColor(item.impactClass)}>{item.impactClass}</Badge>
                  ) : (
                    <Caption1>—</Caption1>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip content="View Detail" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={<ChevronRightRegular />}
                      size="small"
                      aria-label="View response detail"
                      onClick={() => setSelectedResponseId(item.responseId)}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.tabPanel}>
      <div className={styles.toolbarRow}>
        <div className={styles.filterGroup}>
          <Dropdown
            className={styles.filterDropdown}
            placeholder="State"
            value={stateFilter}
            onOptionSelect={(_, data) => {
              setStateFilter((data.optionValue || '') as ResponseActionStatus | '');
              setPage(1);
            }}
          >
            <Option value="">All States</Option>
            {RESPONSE_ACTION_STATUS_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
          </Dropdown>
          <Dropdown
            className={styles.filterDropdown}
            placeholder="Action"
            value={actionFilter}
            onOptionSelect={(_, data) => {
              setActionFilter((data.optionValue || '') as ResponseActionType | '');
              setPage(1);
            }}
          >
            <Option value="">All Actions</Option>
            {RESPONSE_ACTION_TYPE_FILTER_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
          </Dropdown>
          <Input
            className={styles.textFilterInput}
            placeholder="Subject"
            value={subjectInput}
            onChange={(_, data) => {
              setSubjectInput(data.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <ListFrame>{listBody}</ListFrame>
      <Pager
        page={page}
        totalPages={totalPages}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        totalCount={totalCount}
        onPageChange={setPage}
        testId="response-actions-pager"
      />

      <ActionDetailDrawer
        responseId={selectedResponseId}
        onClose={() => setSelectedResponseId(null)}
        toasterId={toasterId}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3 — Policies
// ---------------------------------------------------------------------------

const POLICIES_PAGE_SIZE = 25;

const PoliciesTab: FC<{ toasterId: string }> = ({ toasterId }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);

  const [page, setPage] = useState(1);
  const skip = (page - 1) * POLICIES_PAGE_SIZE;

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['response-policies', skip, POLICIES_PAGE_SIZE],
    queryFn: () => autonomousGovernanceApi.getPolicies(skip, POLICIES_PAGE_SIZE),
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const closeCreateDialog = (force = false) => {
    if (!force && createDirty && !window.confirm('Discard unsaved changes?')) return;
    setIsCreateDialogOpen(false);
    setCreateDirty(false);
  };

  const handlePolicyCreated = (policy: ResponsePolicy) => {
    queryClient.invalidateQueries({ queryKey: ['response-policies'] });
    queryClient.invalidateQueries({ queryKey: ['response-governance-summary'] });
    dispatchToast(
      <Toast>
        <ToastTitle>Policy Created</ToastTitle>
        <ToastBody>{policy.name} is now active.</ToastBody>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    closeCreateDialog(true);
  };

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ externalId, enabled }: { externalId: string; enabled: boolean }) =>
      autonomousGovernanceApi.setPolicyEnabled(externalId, enabled),
    onSuccess: (policy) => {
      queryClient.invalidateQueries({ queryKey: ['response-policies'] });
      queryClient.invalidateQueries({ queryKey: ['response-governance-summary'] });
      dispatchToast(
        <Toast>
          <ToastTitle>{policy.enabled ? 'Policy Enabled' : 'Policy Disabled'}</ToastTitle>
          <ToastBody>{policy.name}</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 4000 }
      );
    },
  });

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(Math.ceil(totalCount / POLICIES_PAGE_SIZE), 1);
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;
  const policies = data?.items ?? [];

  const columnCount = 6; // name, trigger, severity, action, priority, enabled

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load response policies." onRetry={refetch} />;
  } else if (policies.length === 0) {
    listBody = (
      <EmptyState
        icon={<GavelRegular />}
        title="No Response Policies"
        description="Create a policy to start generating automated responses to governance drift, severe REGE verdicts, or risk paths."
        actionLabel="Create Policy"
        onAction={() => setIsCreateDialogOpen(true)}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Response policies">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '100%' }}>Name</TableHeaderCell>
              <TableHeaderCell>Trigger</TableHeaderCell>
              <TableHeaderCell>Min Severity</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Priority</TableHeaderCell>
              <TableHeaderCell>Enabled</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {policies.map((item) => {
              const isToggling = toggleEnabledMutation.isPending && toggleEnabledMutation.variables?.externalId === item.externalId;
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Text weight="semibold">{item.name}</Text>
                    {item.description && (
                      <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                        {item.description}
                      </Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge appearance="outline" color={triggerKindColor(item.appliesToTrigger)}>
                      {item.appliesToTrigger}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={severityColor(item.minSeverity)}>{item.minSeverity}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={actionTypeColor(item.action)}>{item.action}</Badge>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.priority}</Body2>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={item.enabled}
                      disabled={isToggling}
                      label={item.enabled ? 'Enabled' : 'Disabled'}
                      onChange={(_, data2) =>
                        toggleEnabledMutation.mutate({ externalId: item.externalId, enabled: data2.checked })
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.tabPanel}>
      <div className={styles.toolbarRow}>
        <Button appearance="primary" icon={<AddRegular />} onClick={() => setIsCreateDialogOpen(true)}>
          Create Policy
        </Button>
      </div>

      <ListFrame>{listBody}</ListFrame>
      <Pager
        page={page}
        totalPages={totalPages}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        totalCount={totalCount}
        onPageChange={setPage}
        testId="response-policies-pager"
      />

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(_, data2) => (data2.open ? setIsCreateDialogOpen(true) : closeCreateDialog())}
      >
        <DialogSurface className={styles.formDialogSurface}>
          <DialogTitle>Create Policy</DialogTitle>
          <DialogBody>
            <DialogContent>
              <ResponsePolicyForm
                onSuccess={handlePolicyCreated}
                onCancel={() => closeCreateDialog()}
                onDirtyChange={setCreateDirty}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AutonomousGovernancePage
// ---------------------------------------------------------------------------

type PageTab = 'proposals' | 'actions' | 'policies';

export const AutonomousGovernancePage: FC = () => {
  const styles = useStyles();
  const toasterId = useId('autonomous-governance-toaster');

  const [selectedTab, setSelectedTab] = useState<PageTab>('proposals');
  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as PageTab);
  };

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      <PageHeader
        title={crumbTitle}
        subtitle="Automated + human-gated policy responses to governance drift, severe REGE verdicts, and risk paths"
        icon={<GavelRegular />}
      />

      <AutonomousGovernanceSummaryPanel />

      <div className={styles.tabsSection}>
        <div className={styles.tabHeader}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="proposals">Proposals</Tab>
            <Tab value="actions">Action Log</Tab>
            <Tab value="policies">Policies</Tab>
          </TabList>
        </div>

        {selectedTab === 'proposals' && <ProposalsTab toasterId={toasterId} />}
        {selectedTab === 'actions' && <ActionLogTab toasterId={toasterId} />}
        {selectedTab === 'policies' && <PoliciesTab toasterId={toasterId} />}
      </div>
    </div>
  );
};
